import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";
import { HiOutlineViewfinderCircle } from "react-icons/hi2";
import { FiTrash } from "react-icons/fi";
import { Search, ChevronDown, X } from "lucide-react";
import BatchTitleBuilder from "./BatchTitleBuilder";
import { useBatchUploadProgress } from "../../context/BatchUploadProgressContext";
import {
  CHUNK_SIZE,
  uploadFilesInChunks,
  finalizeCardUpload,
} from "../../services/cardUpload";

// Styled Components
const UploadSection = styled.section`
  background: #ededed;
  border-radius: 0;
  border: none;
  border-top: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 2rem;
  box-shadow: none;
  width: 100%;
  min-height: 100%;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: transparent;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }

  &[type="file"] {
    padding: 0.5rem;
    cursor: pointer;
    font-size: 0;
    color: transparent;
    background-color: transparent;

    &:hover,
    &:focus {
      background-color: #e9e9e9;
      border-color: #d1d5db;
      box-shadow: none;
      outline: none;
    }

    &::-webkit-file-upload-button {
      background: #f2f2f2;
      color: #3b82f6;
      border: 1px solid #d1d5db;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      margin-right: 0;
      font-size: 0.875rem;
    }

    &::file-selector-button {
      background: #f2f2f2;
      color: #3b82f6;
      border: 1px solid #d1d5db;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      margin-right: 0;
      font-size: 0.875rem;
    }
  }
`;

const Dropzone = styled.div`
  border: 1px dashed ${(props) => (props.$isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  background: ${(props) => (props.$isDragging ? "#eff6ff" : "transparent")};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: #9ca3af;
    background: #fafafa;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const DropzoneSubtext = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 400;
  color: #9ca3af;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Textarea = styled.textarea`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: transparent;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  font-family: inherit;
  resize: vertical;
  width: 100%;

  &:focus {
    outline: none;
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ImagePreviewSection = styled.div`
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  padding: 1rem;
`;

const ImagePreviewTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(460px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardPairContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #d1d5db;
  border-radius: 12px;
  background: #f9fafb;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImagePreviewItem = styled.div`
  position: relative;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);

    .hover-overlay {
      opacity: 1;
    }
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const ImageDetails = styled.div`
  padding: 0.75rem;
  background: white;
  border-top: 1px solid #f3f4f6;
`;

const ProgressContainer = styled.div`
  margin-top: 0.5rem;
`;

const ProgressBarBackground = styled.div`
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #059669);
  width: ${(props) => props.width || 0}%;
  transition: width 0.25s ease;
`;

const ImageName = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageSize = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
`;

const PreviewBox = styled.div`
  padding: 0.75rem;
  background: #f2f2f2;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  line-height: 2;
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 40px;

  .preview-field-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.8125rem;
    margin: 0 0.125rem;
    user-select: none;
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  color: #374151;
  border: none;
  border-radius: 50%;
  padding: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  svg {
    width: 18px;
    height: 18px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }

  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.view {
    svg {
      color: #2563eb;
      filter: drop-shadow(0 1px 3px rgba(37, 99, 235, 0.3));
    }

    &:hover {
      background: #2563eb;
      svg {
        color: white;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
    }
  }

  &.delete {
    svg {
      color: #dc2626;
      filter: drop-shadow(0 1px 3px rgba(220, 38, 38, 0.3));
    }

    &:hover {
      background: #dc2626;
      svg {
        color: white;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
`;

const PresetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  position: relative;
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 200px;
  background: transparent;
  color: #374151;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #ffffff;
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #3b82f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  background: white;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  z-index: 1000;
  min-width: 300px;
  max-width: 400px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.16);
  background: #ededed;
`;

const DropdownSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PresetCount = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: auto;
`;

const SearchIconWrapper = styled.div`
  color: #6b7280;
  flex-shrink: 0;
`;

const DropdownSearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const DropdownList = styled.div`
  overflow-y: auto;
  max-height: 300px;
`;

const DropdownItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-size: 0.875rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &.selected {
    background: #f3f4f6;
    font-weight: 500;
  }
`;

const DropdownEmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const PresetsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PresetsOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PresetSelectButton = styled.button`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex: 1;
  background: transparent;
  color: #374151;
  text-align: left;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: #ffffff;
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
    color: #6b7280;
  }
`;

const PresetSelectText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BatchSpecificButton = styled.button`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
  background: transparent;
  color: #374151;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: #ffffff;
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedPresetBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1d4ed8;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 100%;
`;

const BadgeRemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-left: auto;

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-width: auto;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &.primary {
    background: #3b82f6;
    color: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:active:not(:disabled) {
      background: #1d4ed8;
    }
  }

  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }

  &.small {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    min-width: 80px;
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

const SuccessMessage = styled.p`
  color: #059669;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
`;

const PresetsForm = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const PresetsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const PresetsSubtitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PresetsActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const MagnifiedViewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  cursor: pointer;
`;

const MagnifiedViewContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: default;
`;

const MagnifiedImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  color: #374151;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: bold;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: ${(props) => (props.large ? "900px" : "500px")};
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ColumnTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid #e5e7eb;
  letter-spacing: -0.01em;
`;

const DropZone = styled.div`
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: visible;
  border: 2px dashed ${(props) => (props.isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  padding: 0.75rem;
  background: ${(props) => (props.isDragging ? "#eff6ff" : "#f9fafb")};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const FieldItem = styled.div`
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: ${(props) => (props.$isInBuilder ? "default" : "move")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;

  &:hover {
    border-color: #000000;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &.dragging {
    opacity: 0.5;
  }

  &.in-builder {
    background: white;
    border-color: #000000;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &.drag-over {
    border-top: 3px solid #000000;
    margin-top: 2px;
  }
`;

const DragHandle = styled.div`
  cursor: move;
  user-select: none;
  display: flex;
  align-items: center;
`;

const FieldName = styled.input`
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  min-width: 300px;
  flex-shrink: 0;
  cursor: ${(props) => (props.$isDraggable ? "move" : "pointer")};
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.4;
  text-align: left;
  pointer-events: auto;
  height: 1.75rem;
  box-sizing: border-box;

  &:hover {
    background: #eff6ff;
  }

  &:focus {
    outline: none;
    background: #eff6ff;
  }
`;

const AvailableFieldName = styled.input`
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  width: 100%;
  cursor: move;
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.4;
  text-align: left;
  pointer-events: auto;
  height: 1.75rem;
  box-sizing: border-box;

  &:hover {
    background: transparent;
  }

  &:focus {
    outline: none;
    background: transparent;
  }
`;

const SuffixInput = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  transition: all 0.2s ease;
  margin-left: 0.5rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FieldInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  padding: 0;
  transition: all 0.2s ease;
  position: relative;
  min-width: 0;
  width: 100%;
  max-width: 100%;

  &.field-input-wrapper:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const FieldNamePrefix = styled.span`
  padding: 0.375rem 0.625rem;
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 600;
  background: #f3f4f6;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  user-select: none;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const FieldInput = styled.textarea`
  flex: 1;
  padding: 0.375rem 0.625rem;
  border: none;
  font-size: 0.8125rem;
  color: #1f2937;
  background: transparent;
  outline: none;
  min-width: 60px;
  min-height: 1.75rem;
  max-height: 150px;
  resize: none;
  overflow-y: auto;
  font-family: inherit;
  line-height: 1.4;

  &[data-input-type="prefix"] {
    border-radius: 6px 0 0 6px;
    border-right: 1px solid #d1d5db;
  }

  &[data-input-type="suffix"] {
    border-radius: 0 6px 6px 0;
    border-left: 1px solid #d1d5db;
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 0.75rem;
  }
`;

const FieldContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.375rem;
  min-width: 0;
  width: 100%;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 0.25rem 0.375rem;
  border-radius: 4px;
  font-size: 1.125rem;
  line-height: 1;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  &:hover {
    background: #fee2e2;
    transform: scale(1.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-style: italic;
`;

const AvailableFieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1.5rem 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const PresetModalFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
  background: white;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  flex: 1;
  margin: 0;
`;

const PresetModalContainer = styled(ModalContainer)`
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  max-height: 70vh;
  max-width: 500px;
`;

const PresetModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
`;

const PresetModalCloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const PresetModalSearchSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PresetModalSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:focus-within {
    background: white;
    border-color: #000000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const CreatePresetLink = styled.button`
  background: transparent;
  border: none;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PresetModalContent = styled.div`
  flex: 1;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const PresetModalSearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.8125rem;
  color: #1f2937;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`;

const PresetModalList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
  overflow-y: auto;
  padding-top: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const PresetModalItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;

  &:hover {
    background: #f9fafb;
    border-color: #000000;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &.selected {
    background: #f3f4f6;
    border-color: #000000;
    font-weight: 500;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &::before {
      content: "✓";
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: #000000;
      font-weight: bold;
      font-size: 1rem;
    }
  }
`;

const PresetModalEmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.8125rem;

  &::before {
    content: "🔍";
    display: block;
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
  }
`;

const GlobalTitleBuilderModalContainer = styled(ModalContainer)`
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

const GlobalTitleBuilderModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const GlobalTitleBuilderModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const GlobalTitleInputContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 80px;
  border: 1px solid ${(props) => (props.isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  background: ${(props) => (props.isDragging ? "#ffffff" : "transparent")};
  padding: 0.75rem;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  cursor: text;

  &:hover,
  &:focus-within {
    background: #ffffff;
    border-color: ${(props) => (props.isDragging ? "#3b82f6" : "#9ca3af")};
  }
`;

const GlobalTitleInput = styled.div`
  width: 100%;
  min-height: auto;
  font-size: 1rem;
  color: #1f2937;
  line-height: 2;
  outline: none;
  border: none;
  background: transparent;
  font-family: inherit;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 0.25rem 0;

  &[contenteditable="true"]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
    pointer-events: none;
  }

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;
    user-select: none;
    cursor: default;
    contenteditable: false;

    .field-badge-remove {
      background: transparent;
      border: none;
      color: #ef4444;
      cursor: pointer;
      padding: 0;
      font-size: 0.875rem;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s ease;
      margin-left: 0.125rem;

      &:hover {
        color: #dc2626;
      }
    }
  }
`;

const GlobalAvailableFieldsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const GlobalFieldItem = styled.div`
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  cursor: move;
  display: flex;
  align-items: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  user-select: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;

  &:hover {
    background: #e5e7eb;
    border-color: rgba(0, 0, 0, 0.4);
  }

  &.dragging {
    opacity: 0.5;
  }
`;

const GlobalPreviewBox = styled.div`
  background: #f2f2f2;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 80px;
  color: #111827;
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: not-allowed;

  .field-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 6px;
    color: #374151;
    font-weight: 500;
    font-size: 0.875rem;
    margin: 0 0.125rem;
  }
`;

const GlobalTitleBuilderModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const BatchSpecificPresetModalContainer = styled(ModalContainer)`
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

const BatchSpecificPresetModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const BatchSpecificPresetModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const BatchSpecificPresetModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const Select = styled.select`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: transparent;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  width: 100%;
  cursor: pointer;
  outline: none;

  &:focus {
    outline: none;
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }
`;

function Upload({ onUploadComplete }) {
  const dispatch = useDispatch();
  const {
    startChunkedUpload,
    updateChunkProgress,
    startBatchProcessing,
  } = useBatchUploadProgress();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError] = useState("");
  const [batchName, setBatchName] = useState("");
  const [titleBuilder, setTitleBuilder] = useState([]); // Array of {field: string, suffix: string} objects in order
  const [globalTitleBuilderConfig, setGlobalTitleBuilderConfig] = useState([]); // Raw configuration array
  const [draggedField, setDraggedField] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDraggingOverDropZone, setIsDraggingOverDropZone] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [availableFields] = useState([
    "Card Name",
    "Type",
    "Manufacturer",
    "Set",
    "Card Number",
    "Year Manufactured",
  ]);
  const [isGlobalTitleBuilderOpen, setIsGlobalTitleBuilderOpen] =
    useState(false);
  const [isBatchTitleBuilderOpen, setIsBatchTitleBuilderOpen] = useState(false);
  const [titleConfigurationType, setTitleConfigurationType] = useState(
    "global-title-builder"
  ); // "global-title-builder" or "batch-specific-title-builder"
  const [globalTitleBuilderPreview, setGlobalTitleBuilderPreview] =
    useState("");
  const [globalTitleBuilderError, setGlobalTitleBuilderError] = useState("");
  const [globalTitleBuilderSuccess, setGlobalTitleBuilderSuccess] =
    useState("");
  const [isSavingGlobalTitleBuilder, setIsSavingGlobalTitleBuilder] =
    useState(false);
  const [isDraggingOverGlobalInput, setIsDraggingOverGlobalInput] =
    useState(false);
  const [isGlobalTitleBuilderReady, setIsGlobalTitleBuilderReady] =
    useState(false);
  const globalTitleInputRef = useRef(null);
  const uploadActionsRef = useRef(null);
  const prevFilesCountRef = useRef(0);
  const [batchTitleBuilderConfig, setBatchTitleBuilderConfig] = useState([]); // New format: [{type: "string" | "field-name", value: "..."}]
  const [defaultMetadata, setDefaultMetadata] = useState(null);
  const [magnifiedImage, setMagnifiedImage] = useState(null);
  const [presetsList, setPresetsList] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isBatchSpecificPresetModalOpen, setIsBatchSpecificPresetModalOpen] =
    useState(false);
  const [batchSpecificPresetName, setBatchSpecificPresetName] = useState("");
  const [batchSpecificPresetFormData, setBatchSpecificPresetFormData] =
    useState({
      presetName: "",
      category: "",
      sportGameFranchise: "",
      manufacturer: "",
      autographed: "",
      graded: "",
      condition: "",
      language: "",
      set: "",
      yearManufactured: "",
      vintage: "",
      conditionDescription: "",
      country: "",
    });
  const [isCreatePresetModalOpen, setIsCreatePresetModalOpen] = useState(false);
  const [createPresetFormData, setCreatePresetFormData] = useState({
    presetName: "",
    category: "",
    sportGameFranchise: "",
    manufacturer: "",
    autographed: "",
    graded: "",
    condition: "",
    language: "",
    set: "",
    yearManufactured: "",
    vintage: "",
    conditionDescription: "",
    country: "",
  });
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [presetsDraft, setPresetsDraft] = useState({
    Category: "",
    Condition: "",
    eBayListing: {
      Category: "",
      Condition: "",
      "Condition Description": "",
      ItemSpecifics: {
        Brand: "",
        "Card Name": "",
        Set: "",
        SubSet: "",
        Variant: "",
        Year: "",
        Rarity: "",
        "Country of Origin": "",
      },
    },
  });

  const errorRef = useRef(null);

  const DEFAULT_BASE_URL = "http://localhost:8000";

  const API_BASE_URL =
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL;

  // Label for the category-specific field (Sport/Game/Franchise) shown after a category is selected
  const CATEGORY_SPECIFIC_FIELD_LABEL = {
    "Sport Trading Card": "Sport",
    "Non-Sport Trading Card": "Franchise",
    "CCG Individual Card": "Game",
    "Collectible Sports Sticker Singles": "Sport",
  };

  // Get condition options based on category
  const getConditionOptions = (category) => {
    if (category === "CCG Individual Card") {
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
      ];
    } else if (
      category === "Sport Trading Card" ||
      category === "Non-Sport Trading Card" ||
      category === "Collectible Sports Sticker Singles" ||
      category === "Collectible Non-Sport Sticker Singles"
    ) {
      return ["Near Mint or Better", "Excellent", "Very Good", "Poor"];
    } else {
      // Default: show all options if category is not set or unknown
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
        "Excellent",
        "Very Good",
        "Poor",
      ];
    }
  };

  // Token refresh helper
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  // Clear condition if it's not valid for the selected category (Batch Specific Preset)
  useEffect(() => {
    if (
      batchSpecificPresetFormData.category &&
      batchSpecificPresetFormData.condition
    ) {
      const validOptions = getConditionOptions(
        batchSpecificPresetFormData.category
      );
      if (!validOptions.includes(batchSpecificPresetFormData.condition)) {
        setBatchSpecificPresetFormData((prev) => ({
          ...prev,
          condition: "",
        }));
      }
    }
  }, [batchSpecificPresetFormData.category]);

  // Clear condition if it's not valid for the selected category (Create Preset)
  useEffect(() => {
    if (createPresetFormData.category && createPresetFormData.condition) {
      const validOptions = getConditionOptions(createPresetFormData.category);
      if (!validOptions.includes(createPresetFormData.condition)) {
        setCreatePresetFormData((prev) => ({
          ...prev,
          condition: "",
        }));
      }
    }
  }, [createPresetFormData.category]);

  // Load presets list from API on component mount
  useEffect(() => {
    const loadPresetsList = async () => {
      try {
        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        let response = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          const newToken = await refreshAccessToken();
          response = await fetch(
            `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (response.ok) {
          const data = await response.json();
          // Handle both array and paginated responses
          let presetsArray = [];
          if (Array.isArray(data)) {
            presetsArray = data;
          } else if (data.results && Array.isArray(data.results)) {
            presetsArray = data.results;
          } else if (data.id) {
            presetsArray = [data];
          }
          setPresetsList(presetsArray);
        } else {
          console.error("Failed to load presets list:", response.statusText);
        }
      } catch (error) {
        console.error("Error loading presets list:", error);
      }
    };

    loadPresetsList();
  }, [API_BASE_URL]);

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      // Validate new files
      const validation = validateFiles(newFiles);
      if (!validation.ok) {
        setError(validation.message);
        setUploadStatus("");
        // Reset file input
        event.target.value = "";
        return;
      }

      // Combine existing files with new files, avoiding duplicates
      setSelectedFiles((prevFiles) => {
        const existingFileNames = new Set(prevFiles.map((f) => f.name));
        const uniqueNewFiles = newFiles.filter(
          (f) => !existingFileNames.has(f.name)
        );
        const combinedFiles = [...prevFiles, ...uniqueNewFiles];

        // Validate combined files (check even number requirement)
        if (combinedFiles.length % 2 !== 0) {
          setError(
            "Total number of files must be even (front & back for each card)."
          );
          setUploadStatus("");
          // Reset file input
          event.target.value = "";
          return prevFiles;
        }

        // Check total file size limit (optional - you can adjust this)
        const MAX_TOTAL_SIZE_MB = 500; // 500 MB total limit
        const totalSize = combinedFiles.reduce((sum, f) => sum + f.size, 0);
        if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
          setError(`Total file size exceeds ${MAX_TOTAL_SIZE_MB} MB limit.`);
          setUploadStatus("");
          // Reset file input
          event.target.value = "";
          return prevFiles;
        }

        setError("");
        setUploadStatus("");
        return combinedFiles;
      });

      // Reset file input to allow selecting the same files again if needed
      event.target.value = "";
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDropzoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(true);
  };

  const handleDropzoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
  };

  const handleDropzoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length === 0 || !fileInputRef.current) return;

    const dataTransfer = new DataTransfer();
    droppedFiles.forEach((file) => dataTransfer.items.add(file));
    fileInputRef.current.files = dataTransfer.files;
    handleFileSelect({ target: fileInputRef.current });
  };

  const pruneEmptyDeep = (value) => {
    if (value == null) return undefined;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }
    if (Array.isArray(value)) {
      const prunedArr = value
        .map(pruneEmptyDeep)
        .filter((v) => v !== undefined);
      return prunedArr.length > 0 ? prunedArr : undefined;
    }
    if (typeof value === "object") {
      const result = {};
      Object.entries(value).forEach(([k, v]) => {
        const pruned = pruneEmptyDeep(v);
        if (pruned !== undefined) result[k] = pruned;
      });
      return Object.keys(result).length > 0 ? result : undefined;
    }
    return value;
  };

  // Preset management functions
  const handleSetPresets = () => {
    setIsPresetModalOpen(true);
  };

  const handleClosePresetModal = () => {
    setIsPresetModalOpen(false);
    setSearchQuery("");
  };

  const handleSelectPreset = async (preset) => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Fetch full preset data
      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/preset/${preset.id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/${preset.id}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const presetData = await response.json();

        // Convert preset_data to the nested structure expected by upload
        // Normalize preset_data like batch preset does
        const convertedPresets = {
          Category: presetData.preset_data?.ebayCategory || "",
          Condition: presetData.preset_data?.ebayCondition || "",
          eBayListing: {
            Title: presetData.preset_data?.ebayTitle || "",
            Category: presetData.preset_data?.ebayCategory || "",
            Description: presetData.preset_data?.ebayDescription || "",
            Condition: presetData.preset_data?.ebayCondition || "",
            "Condition Description":
              presetData.preset_data?.ebayConditionDescription || "",
            ItemSpecifics: {
              Brand: presetData.preset_data?.ebayManufacturer || presetData.preset_data?.ebayBrand || "",
              "Card Name": presetData.preset_data?.ebayCardName || "",
              Set: presetData.preset_data?.ebaySet || "",
              SubSet: presetData.preset_data?.ebaySubset || "",
              Variant: presetData.preset_data?.ebayVariant || "",
              Year: presetData.preset_data?.ebayYear || presetData.preset_data?.ebayYearManufactured || "",
              "Year Manufactured": presetData.preset_data?.ebayYearManufactured || "",
              "Card Number": "",
              Rarity: presetData.preset_data?.ebayRarity || "",
              "Country of Origin": presetData.preset_data?.ebayCountry || "",
              Language: presetData.preset_data?.ebayLanguage || "",
              Autographed: presetData.preset_data?.ebayAutographed || "",
              Graded: presetData.preset_data?.ebayGraded || "",
              Vintage: presetData.preset_data?.ebayVintage || "",
              ...(CATEGORY_SPECIFIC_FIELD_LABEL[presetData.preset_data?.ebayCategory] && {
                [CATEGORY_SPECIFIC_FIELD_LABEL[presetData.preset_data?.ebayCategory]]:
                  presetData.preset_data?.ebaySportGameFranchise || "",
              }),
            },
          },
        };

        setPresetsDraft(convertedPresets);
        setDefaultMetadata(convertedPresets);
        setSelectedPreset(preset);
        setBatchSpecificPresetName(""); // Clear batch-specific preset name when selecting from list
        setIsPresetModalOpen(false);
        setSearchQuery("");
      }
    } catch (error) {
      console.error("Error loading preset:", error);
    }
  };

  const handleClearPresets = () => {
    setDefaultMetadata(null);
    setSelectedPreset(null);
    setBatchSpecificPresetName("");
    handleResetBatchSpecificPresetForm();
    setPresetsDraft({
      Category: "",
      Condition: "",
      eBayListing: {
        Category: "",
        Condition: "",
        "Condition Description": "",
        ItemSpecifics: {
          Brand: "",
          "Card Name": "",
          Set: "",
          SubSet: "",
          Variant: "",
          Year: "",
          Rarity: "",
          "Country of Origin": "",
        },
      },
    });
  };

  // Batch-specific preset handlers
  const handleOpenBatchSpecificPresetModal = () => {
    setIsBatchSpecificPresetModalOpen(true);
  };

  const handleCloseBatchSpecificPresetModal = () => {
    setIsBatchSpecificPresetModalOpen(false);
  };

  const handleBatchSpecificPresetInputChange = (field, value) => {
    setBatchSpecificPresetFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetBatchSpecificPresetForm = () => {
    setBatchSpecificPresetFormData({
      presetName: "",
      category: "",
      sportGameFranchise: "",
      manufacturer: "",
      autographed: "",
      graded: "",
      condition: "",
      language: "",
      set: "",
      yearManufactured: "",
      vintage: "",
      conditionDescription: "",
      country: "",
    });
  };

  const handleSaveBatchSpecificPreset = () => {
    // Convert form data to the same structure as when selecting a preset
    const convertedPresets = {
      Category: batchSpecificPresetFormData.category || "",
      Condition: batchSpecificPresetFormData.condition || "",
      eBayListing: {
        Category: batchSpecificPresetFormData.category || "",
        Condition: batchSpecificPresetFormData.condition || "",
        "Condition Description":
          batchSpecificPresetFormData.conditionDescription || "",
        ItemSpecifics: {
          Brand: batchSpecificPresetFormData.manufacturer || "",
          "Card Name": "",
          Set: batchSpecificPresetFormData.set || "",
          SubSet: "",
          Variant: "",
          Year: batchSpecificPresetFormData.yearManufactured || "",
          "Year Manufactured":
            batchSpecificPresetFormData.yearManufactured || "",
          "Card Number": "",
          Rarity: "",
          "Country of Origin": batchSpecificPresetFormData.country || "",
          Language: batchSpecificPresetFormData.language || "",
          Autographed: batchSpecificPresetFormData.autographed || "",
          Graded: batchSpecificPresetFormData.graded || "",
          Vintage: batchSpecificPresetFormData.vintage || "",
          ...(CATEGORY_SPECIFIC_FIELD_LABEL[batchSpecificPresetFormData.category] && {
            [CATEGORY_SPECIFIC_FIELD_LABEL[batchSpecificPresetFormData.category]]:
              batchSpecificPresetFormData.sportGameFranchise || "",
          }),
        },
      },
    };

    setPresetsDraft(convertedPresets);
    setDefaultMetadata(convertedPresets);
    setSelectedPreset(null); // Clear selected preset since we're using batch-specific
    setBatchSpecificPresetName(
      batchSpecificPresetFormData.presetName || "Batch Specific Preset"
    );
    setIsBatchSpecificPresetModalOpen(false);
  };

  // Filter presets based on search query
  const filteredPresets = presetsList.filter((preset) =>
    (preset.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Create preset handlers
  const handleOpenCreatePresetModal = () => {
    setIsCreatePresetModalOpen(true);
    setIsPresetModalOpen(false); // Close the select preset modal
  };

  const handleCloseCreatePresetModal = () => {
    setIsCreatePresetModalOpen(false);
    handleResetCreatePresetForm();
  };

  const handleCreatePresetInputChange = (field, value) => {
    setCreatePresetFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResetCreatePresetForm = () => {
    setCreatePresetFormData({
      presetName: "",
      category: "",
      sportGameFranchise: "",
      manufacturer: "",
      autographed: "",
      graded: "",
      condition: "",
      language: "",
      set: "",
      yearManufactured: "",
      vintage: "",
      conditionDescription: "",
      country: "",
    });
  };

  const handleSaveCreatePreset = async () => {
    // Validate preset name
    if (!createPresetFormData.presetName.trim()) {
      setError("Please provide a preset name");
      return;
    }

    setIsSavingPreset(true);
    setError("");

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Convert form data to API format
      const presetPayload = {
        name: createPresetFormData.presetName.trim(),
        preset_data: {
          ebayCategory: createPresetFormData.category || "",
          ebaySportGameFranchise: createPresetFormData.sportGameFranchise || "",
          ebayBrand: createPresetFormData.manufacturer || "",
          ebayAutographed: createPresetFormData.autographed || "",
          ebayGraded: createPresetFormData.graded || "",
          ebayCondition: createPresetFormData.condition || "",
          ebayLanguage: createPresetFormData.language || "",
          ebaySet: createPresetFormData.set || "",
          ebayYear: createPresetFormData.yearManufactured || "",
          ebayVintage: createPresetFormData.vintage || "",
          ebayConditionDescription:
            createPresetFormData.conditionDescription || "",
          ebayCountry: createPresetFormData.country || "",
        },
      };

      let response = await fetch(`${API_BASE_URL}/api/v1/cards/preset/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(presetPayload),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(`${API_BASE_URL}/api/v1/cards/preset/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(presetPayload),
        });
      }

      if (response.ok) {
        const newPreset = await response.json();

        // Refresh presets list
        let listResponse = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (listResponse.status === 401) {
          const newToken = await refreshAccessToken();
          listResponse = await fetch(
            `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (listResponse.ok) {
          const data = await listResponse.json();
          let presetsArray = [];
          if (Array.isArray(data)) {
            presetsArray = data;
          } else if (data.results && Array.isArray(data.results)) {
            presetsArray = data.results;
          } else if (data.id) {
            presetsArray = [data];
          }
          setPresetsList(presetsArray);
        }

        // Close the create preset modal and reset form
        setIsCreatePresetModalOpen(false);
        handleResetCreatePresetForm();

        // Open the select preset modal first
        setIsPresetModalOpen(true);
        setSearchQuery(""); // Clear search to show all presets

        // Wait a moment for the modal to render, then select the preset
        setTimeout(async () => {
          // Select the newly created preset (this will apply it and close the modal)
          await handleSelectPreset(newPreset);
        }, 300);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail ||
          errorData.message ||
          `Failed to create preset: ${response.statusText}`;
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.message || "Failed to create preset. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSavingPreset(false);
    }
  };

  // Build finalize payload per v1 API spec
  const buildFinalizePayload = () => {
    const payload = {
      batch_name: batchName || "Default Batch",
      titleBuilderData: [],
    };

    if (defaultMetadata) {
      const pruned = pruneEmptyDeep(defaultMetadata);
      if (pruned) payload.default_metadata = pruned;
    }

    if (titleConfigurationType === "global-title-builder") {
      payload.titleBuilderData = globalTitleBuilderConfig;
    } else if (batchTitleBuilderConfig.length > 0) {
      payload.titleBuilderData = batchTitleBuilderConfig;
    } else {
      payload.titleBuilderData = titleBuilder
        .map((item) => {
          const fieldName = typeof item === "string" ? item : item.field;
          const prefix =
            typeof item === "string" ? "" : (item.prefix || "").trim();
          const suffix =
            typeof item === "string" ? "" : (item.suffix || "").trim();

          const parts = [];
          if (prefix) parts.push({ type: "string", value: prefix });
          parts.push({ type: "field-name", value: fieldName });
          if (suffix) parts.push({ type: "string", value: suffix });

          return parts;
        })
        .flat();
    }

    return payload;
  };

  const handleDragStart = (e, field, index = null) => {
    setDraggedField(field);
    setDraggedIndex(index);
    e.dataTransfer.setData("text/plain", field);
    e.dataTransfer.effectAllowed = "move";
    if (e.target) {
      e.target.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = "1";
    }
    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingOverDropZone(false);
  };

  const handleDragOver = (e, index = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== null) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    setTitleBuilder((prev) => {
      // If dragging within the builder (reordering)
      if (draggedIndex !== null) {
        const newArray = [...prev];
        const [removed] = newArray.splice(draggedIndex, 1);
        // Adjust target index if dragging from before target
        const adjustedIndex =
          draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        newArray.splice(adjustedIndex, 0, removed);
        return newArray;
      } else {
        // If dragging from available fields
        const getFieldName = (item) =>
          typeof item === "string" ? item : item.field;
        const filtered = prev.filter((item) => getFieldName(item) !== field);
        const newArray = [...filtered];
        newArray.splice(targetIndex, 0, { field, prefix: "", suffix: "" });
        return newArray;
      }
    });

    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDropToBuilder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    // Only add if not already in builder
    const getFieldName = (item) =>
      typeof item === "string" ? item : item.field;
    const fieldExists = titleBuilder.some(
      (item) => getFieldName(item) === field
    );
    if (!fieldExists) {
      setTitleBuilder((prev) => [...prev, { field, prefix: "", suffix: "" }]);
    }

    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingOverDropZone(false);
  };

  const handleRemoveField = (indexToRemove) => {
    setTitleBuilder((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handlePrefixChange = (index, prefix) => {
    setTitleBuilder((prev) => {
      const newArray = [...prev];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix, suffix: "" };
      } else {
        const currentSuffix = typeof item === "string" ? "" : item.suffix || "";
        newArray[index] = { ...item, prefix, suffix: currentSuffix };
      }
      return newArray;
    });
  };

  const handleSuffixChange = (index, suffix) => {
    setTitleBuilder((prev) => {
      const newArray = [...prev];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix: "", suffix };
      } else {
        const currentPrefix = typeof item === "string" ? "" : item.prefix || "";
        newArray[index] = { ...item, prefix: currentPrefix, suffix };
      }
      return newArray;
    });
  };

  const handleFieldNameClick = (index, e) => {
    e.stopPropagation();
    setEditingFieldIndex(index);
  };

  const handleInputBlur = (e) => {
    // Don't close if focus is moving to another input in the same field
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    // Check if focus is moving to another textarea in the same wrapper
    if (relatedTarget && currentTarget) {
      const wrapper = currentTarget.closest(".field-input-wrapper");
      if (wrapper && wrapper.contains(relatedTarget)) {
        // Focus is moving within the same wrapper, don't close
        return;
      }
    }

    // Small delay to allow focus to move between inputs
    setTimeout(() => {
      // Check if any input in this field still has focus
      const wrapper = e.target.closest(".field-input-wrapper");
      if (wrapper) {
        const hasFocus = wrapper.querySelector("textarea:focus");
        if (!hasFocus) {
          setEditingFieldIndex(null);
        }
      } else {
        setEditingFieldIndex(null);
      }
    }, 150);
  };

  // Example values for preview
  const getExampleValue = (fieldName) => {
    const examples = {
      "Card Name": "Pikachu",
      Type: "Electric",
      Manufacturer: "Pokemon",
      Set: "Base Set",
      "Card Number": "25",
      "Year Manufactured": "1999",
    };
    return examples[fieldName] || fieldName;
  };

  // Generate preview HTML from configuration with field names as badges
  const generatePreviewFromConfig = (configuration) => {
    if (
      !configuration ||
      !Array.isArray(configuration) ||
      configuration.length === 0
    ) {
      return "No configuration set";
    }

    let previewHTML = "";
    configuration.forEach((item) => {
      if (item.type === "string") {
        // Escape HTML in text
        const escapedText = (item.value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
        previewHTML += escapedText;
      } else if (item.type === "field-name") {
        // Show field name as a badge/tag
        const fieldName = item.value || "";
        const escapedFieldName = fieldName
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
        previewHTML += `<span class="preview-field-badge">${escapedFieldName}</span>`;
      }
    });

    return previewHTML || "No configuration set";
  };

  // Generate preview HTML from preset metadata
  const generatePresetPreview = (metadata) => {
    if (!metadata) {
      return "No preset selected";
    }

    const escapeHTML = (text) => {
      if (!text) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    let previewHTML = "";

    // Basic fields
    if (metadata.Category) {
      previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Category:</strong> ${escapeHTML(metadata.Category)}</div>`;
    }
    if (metadata.Condition) {
      previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Condition:</strong> ${escapeHTML(metadata.Condition)}</div>`;
    }

    // eBay Listing fields
    if (metadata.eBayListing) {
      if (metadata.eBayListing.Category) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>eBay Category:</strong> ${escapeHTML(metadata.eBayListing.Category)}</div>`;
      }
      if (metadata.eBayListing.Condition) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>eBay Condition:</strong> ${escapeHTML(metadata.eBayListing.Condition)}</div>`;
      }
      if (metadata.eBayListing["Condition Description"]) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Condition Description:</strong> ${escapeHTML(metadata.eBayListing["Condition Description"])}</div>`;
      }

      // Item Specifics
      if (metadata.eBayListing.ItemSpecifics) {
        const itemSpecifics = metadata.eBayListing.ItemSpecifics;
        const filledSpecifics = Object.entries(itemSpecifics).filter(
          ([key, value]) => value && String(value).trim() !== ""
        );

        if (filledSpecifics.length > 0) {
          previewHTML += `<div style="margin-top: 0.75rem; margin-bottom: 0.5rem;"><strong>Item Specifics:</strong></div>`;
          filledSpecifics.forEach(([key, value]) => {
            previewHTML += `<div style="margin-left: 1rem; margin-bottom: 0.25rem;"><strong>${escapeHTML(key)}:</strong> ${escapeHTML(value)}</div>`;
          });
        }
      }
    }

    return previewHTML || "No preset data available";
  };

  // Parse configuration from API response back into titleBuilder format
  const parseConfiguration = (configuration) => {
    if (!configuration || !Array.isArray(configuration)) {
      return [];
    }

    return configuration
      .map((item) => {
        // Handle new format: { type: "string" or "field-name", value: "..." }
        if (item && typeof item === "object" && !Array.isArray(item)) {
          if (item.type === "field-name") {
            return {
              field: item.value || "",
              prefix: "",
              suffix: "",
            };
          } else if (item.type === "string") {
            // For string type, we'll just return it as text (might need special handling)
            return {
              field: item.value || "",
              prefix: "",
              suffix: "",
            };
          }

          // Handle old format: { prefix: "", fieldName: "", suffix: "" }
          const fieldName = item.fieldName || item.field || "";
          const prefix = item.prefix || "";
          const suffix = item.suffix || "";

          if (!fieldName) {
            return null;
          }

          return {
            field: fieldName.trim(),
            prefix: (prefix || "").trim(),
            suffix: (suffix || "").trim(),
          };
        }

        // Handle legacy format: ["prefix fieldName suffix"] (for backward compatibility)
        if (Array.isArray(item) && item.length > 0) {
          const combinedString = item[0];
          if (typeof combinedString !== "string") {
            return null;
          }

          const trimmedString = combinedString.trim();
          if (!trimmedString) {
            return null;
          }

          // Try to match field names from availableFields
          const sortedFields = [...availableFields].sort(
            (a, b) => b.length - a.length
          );

          let matchedField = null;
          let matchedIndex = -1;

          for (const field of sortedFields) {
            const index = trimmedString.indexOf(field);
            if (index !== -1) {
              matchedField = field;
              matchedIndex = index;
              break;
            }
          }

          if (matchedField && matchedIndex !== -1) {
            const prefix = trimmedString.substring(0, matchedIndex).trim();
            const suffix = trimmedString
              .substring(matchedIndex + matchedField.length)
              .trim();

            return {
              field: matchedField,
              prefix: prefix,
              suffix: suffix,
            };
          }

          return {
            field: trimmedString,
            prefix: "",
            suffix: "",
          };
        }

        return null;
      })
      .filter((item) => item !== null);
  };

  // Fetch global title builder configuration when selected
  useEffect(() => {
    const fetchGlobalTitleBuilder = async () => {
      if (titleConfigurationType !== "global-title-builder") {
        // Clear titleBuilder when switching away from global
        if (titleConfigurationType === "batch-specific-title-builder") {
          setTitleBuilder([]);
        }
        return;
      }

      try {
        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        let response = await fetch(
          `${API_BASE_URL}/api/v1/cards/title-builder/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          const newToken = await refreshAccessToken();
          response = await fetch(
            `${API_BASE_URL}/api/v1/cards/title-builder/`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (response.ok) {
          const data = await response.json();
          if (data.configuration && Array.isArray(data.configuration)) {
            // Store raw configuration for preview
            setGlobalTitleBuilderConfig(data.configuration);
            const parsedConfig = parseConfiguration(data.configuration);
            setTitleBuilder(parsedConfig);
          } else {
            setGlobalTitleBuilderConfig([]);
            setTitleBuilder([]);
          }
        } else if (response.status === 404) {
          // No configuration saved yet, that's okay
          setGlobalTitleBuilderConfig([]);
          setTitleBuilder([]);
        } else {
          console.error(
            "Failed to load global title builder configuration:",
            response.statusText
          );
          setGlobalTitleBuilderConfig([]);
          setTitleBuilder([]);
        }
      } catch (err) {
        console.error(
          "Error fetching global title builder configuration:",
          err
        );
        setTitleBuilder([]);
      }
    };

    fetchGlobalTitleBuilder();
  }, [titleConfigurationType, API_BASE_URL]);

  // Reset modal readiness when it closes
  useEffect(() => {
    if (!isGlobalTitleBuilderOpen) {
      setIsGlobalTitleBuilderReady(false);
    }
  }, [isGlobalTitleBuilderOpen]);

  // Auto-focus input field when modal is ready
  useEffect(() => {
    if (
      isGlobalTitleBuilderOpen &&
      isGlobalTitleBuilderReady &&
      globalTitleInputRef.current
    ) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (globalTitleInputRef.current) {
            globalTitleInputRef.current.focus();
            // Place cursor at the end of content if there's any
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(globalTitleInputRef.current);
            range.collapse(false); // Collapse to end
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 100);
      });
    }
  }, [isGlobalTitleBuilderOpen, isGlobalTitleBuilderReady]);

  // Auto-resize textarea when editing
  useEffect(() => {
    if (editingFieldIndex !== null) {
      const textareas = document.querySelectorAll(
        `textarea[data-field-index="${editingFieldIndex}"]`
      );
      textareas.forEach((textarea) => {
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });
    }
  }, [editingFieldIndex, titleBuilder]);

  // Auto-resize field name textareas (non-editing mode)
  useEffect(() => {
    const fieldNameTextareas = document.querySelectorAll("textarea[readonly]");
    fieldNameTextareas.forEach((textarea) => {
      if (textarea && !textarea.closest(".field-input-wrapper")) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    });
  }, [titleBuilder]);

  // Global Title Builder handlers
  // Rebuild contentEditable from configuration array
  const rebuildGlobalTitleBuilderFromConfiguration = (configuration) => {
    if (!globalTitleInputRef.current || !Array.isArray(configuration)) return;

    // Clear existing content
    globalTitleInputRef.current.innerHTML = "";

    // Rebuild content from configuration
    configuration.forEach((item) => {
      if (item.type === "string") {
        const textNode = document.createTextNode(item.value || "");
        globalTitleInputRef.current.appendChild(textNode);
      } else if (item.type === "field-name") {
        const fieldBadge = document.createElement("span");
        fieldBadge.className = "field-badge";
        fieldBadge.setAttribute("contenteditable", "false");
        fieldBadge.setAttribute("data-field", item.value || "");

        const fieldNameSpan = document.createElement("span");
        fieldNameSpan.textContent = item.value || "";
        fieldBadge.appendChild(fieldNameSpan);

        const removeButton = document.createElement("button");
        removeButton.className = "field-badge-remove";
        removeButton.textContent = "×";
        removeButton.setAttribute("contenteditable", "false");
        removeButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          removeGlobalFieldBadge(fieldBadge);
        };
        fieldBadge.appendChild(removeButton);

        globalTitleInputRef.current.appendChild(fieldBadge);
      }
    });

    updateGlobalTitlePreview();
  };

  // Remove field badge
  const removeGlobalFieldBadge = (badgeElement) => {
    if (!badgeElement || !globalTitleInputRef.current) return;

    const textNode = document.createTextNode("");
    badgeElement.parentNode.replaceChild(textNode, badgeElement);

    const selection = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updateGlobalTitlePreview();
  };

  // Insert field at cursor position
  const insertGlobalFieldAtCursor = (fieldName) => {
    if (!globalTitleInputRef.current) return;

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(globalTitleInputRef.current);
      range.collapse(false);
    }

    const fieldBadge = document.createElement("span");
    fieldBadge.className = "field-badge";
    fieldBadge.setAttribute("contenteditable", "false");
    fieldBadge.setAttribute("data-field", fieldName);

    const fieldNameSpan = document.createElement("span");
    fieldNameSpan.textContent = fieldName;
    fieldBadge.appendChild(fieldNameSpan);

    const removeButton = document.createElement("button");
    removeButton.className = "field-badge-remove";
    removeButton.textContent = "×";
    removeButton.setAttribute("contenteditable", "false");
    removeButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      removeGlobalFieldBadge(fieldBadge);
    };
    fieldBadge.appendChild(removeButton);

    range.deleteContents();
    range.insertNode(fieldBadge);

    range.setStartAfter(fieldBadge);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    updateGlobalTitlePreview();
  };

  // Generate example preview HTML
  const generateGlobalPreview = () => {
    if (!globalTitleInputRef.current) {
      return "Your title preview will appear here...";
    }

    const configuration = formatGlobalConfiguration();
    if (configuration.length === 0) {
      return "Your title preview will appear here...";
    }

    let previewText = "";
    configuration.forEach((item) => {
      if (item.type === "string") {
        previewText += item.value;
      } else if (item.type === "field-name") {
        const exampleValue = getExampleValue(item.value);
        previewText += exampleValue;
      }
    });

    const escapedText = previewText
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    return escapedText || "Your title preview will appear here...";
  };

  // Update preview
  const updateGlobalTitlePreview = () => {
    if (!globalTitleInputRef.current) {
      setGlobalTitleBuilderPreview("");
      return;
    }

    setGlobalTitleBuilderPreview(generateGlobalPreview());
  };

  // Format configuration for API
  const formatGlobalConfiguration = () => {
    if (!globalTitleInputRef.current) return [];

    const configuration = [];

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          configuration.push({
            type: "string",
            value: text,
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.classList && node.classList.contains("field-badge")) {
          const fieldName = node.getAttribute("data-field");
          if (fieldName) {
            configuration.push({
              type: "field-name",
              value: fieldName,
            });
          }
        } else {
          for (let child = node.firstChild; child; child = child.nextSibling) {
            processNode(child);
          }
        }
      }
    };

    for (
      let child = globalTitleInputRef.current.firstChild;
      child;
      child = child.nextSibling
    ) {
      processNode(child);
    }

    return configuration;
  };

  // Load global title builder configuration
  useEffect(() => {
    if (!isGlobalTitleBuilderOpen) return;

    const loadGlobalTitleBuilder = async () => {
      setGlobalTitleBuilderError("");
      setGlobalTitleBuilderSuccess("");
      setIsGlobalTitleBuilderReady(false);

      try {
        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        let response = await fetch(
          `${API_BASE_URL}/api/v1/cards/title-builder/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          const newToken = await refreshAccessToken();
          response = await fetch(
            `${API_BASE_URL}/api/v1/cards/title-builder/`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${newToken}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (response.ok) {
          const data = await response.json();
          if (data.configuration && Array.isArray(data.configuration)) {
            // Use requestAnimationFrame to ensure DOM is ready, then rebuild
            requestAnimationFrame(() => {
              setTimeout(() => {
                rebuildGlobalTitleBuilderFromConfiguration(data.configuration);
                setGlobalTitleBuilderConfig(data.configuration);
                // Mark as ready after a small delay to ensure all handlers are attached
                setTimeout(() => {
                  setIsGlobalTitleBuilderReady(true);
                }, 50);
              }, 100);
            });
          } else {
            // No configuration, but still mark as ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                setIsGlobalTitleBuilderReady(true);
              }, 50);
            });
          }
        } else if (response.status === 404) {
          // No configuration saved yet, mark as ready
          requestAnimationFrame(() => {
            setTimeout(() => {
              setIsGlobalTitleBuilderReady(true);
            }, 50);
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          setGlobalTitleBuilderError(
            errorData.detail ||
              errorData.message ||
              `Failed to load configuration: ${response.statusText}`
          );
          // Still mark as ready even on error
          requestAnimationFrame(() => {
            setTimeout(() => {
              setIsGlobalTitleBuilderReady(true);
            }, 50);
          });
        }
      } catch (err) {
        console.error(
          "Error fetching global title builder configuration:",
          err
        );
        // Mark as ready even on error
        requestAnimationFrame(() => {
          setTimeout(() => {
            setIsGlobalTitleBuilderReady(true);
          }, 50);
        });
      }
    };

    loadGlobalTitleBuilder();
  }, [isGlobalTitleBuilderOpen, API_BASE_URL]);

  // Handle global title builder input
  const handleGlobalTitleInput = () => {
    updateGlobalTitlePreview();
  };

  // Handle global title builder drag over (only on input field)
  const handleGlobalDragOver = (e) => {
    if (!isGlobalTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOverGlobalInput(true);
  };

  // Handle global title builder drag leave (only on input field)
  const handleGlobalDragLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    // Don't change state if we're moving to a child element (like a field badge)
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    setIsDraggingOverGlobalInput(false);
  };

  // Handle global title builder drop (only on input field)
  const handleGlobalDrop = (e) => {
    if (!isGlobalTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverGlobalInput(false);

    const field = e.dataTransfer.getData("text/plain");
    if (field) {
      insertGlobalFieldAtCursor(field);
    }
  };

  // Prevent drops on container (only allow on input itself)
  const handleGlobalContainerDragOver = (e) => {
    if (!isGlobalTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Don't set isDraggingOverGlobalInput here - only on the input itself
  };

  const handleGlobalContainerDrop = (e) => {
    if (!isGlobalTitleBuilderReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Prevent drop on container - only allow on input
  };

  // Handle global title builder paste
  const handleGlobalPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      updateGlobalTitlePreview();
    }
  };

  // Clear global title builder
  const handleClearGlobalTitleBuilder = () => {
    if (globalTitleInputRef.current) {
      globalTitleInputRef.current.innerHTML = "";
      setGlobalTitleBuilderPreview("");
      setGlobalTitleBuilderError("");
      setGlobalTitleBuilderSuccess("");
    }
  };

  // Save global title builder
  const handleSaveGlobalTitleBuilder = async () => {
    const configuration = formatGlobalConfiguration();

    if (configuration.length === 0) {
      setGlobalTitleBuilderError(
        "Please add at least one field to save the configuration."
      );
      setGlobalTitleBuilderSuccess("");
      return;
    }

    setIsSavingGlobalTitleBuilder(true);
    setGlobalTitleBuilderError("");
    setGlobalTitleBuilderSuccess("");

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const payload = {
        configuration: configuration,
      };

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/title-builder/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(`${API_BASE_URL}/api/v1/cards/title-builder/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        setGlobalTitleBuilderSuccess(
          "Title builder settings saved successfully!"
        );
        setGlobalTitleBuilderError("");
        setGlobalTitleBuilderConfig(configuration);
        // Refresh the global title builder preview in the main view
        if (titleConfigurationType === "global-title-builder") {
          setTitleBuilder(parseConfiguration(configuration));
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail ||
          errorData.message ||
          `Failed to save settings: ${response.statusText}`;
        setGlobalTitleBuilderError(errorMessage);
        setGlobalTitleBuilderSuccess("");
      }
    } catch (err) {
      const errorMessage =
        err.message || "Failed to save settings. Please try again.";
      setGlobalTitleBuilderError(errorMessage);
      setGlobalTitleBuilderSuccess("");
    } finally {
      setIsSavingGlobalTitleBuilder(false);
    }
  };

  // Handle opening title builder modal
  const handleOpenTitleBuilder = () => {
    if (titleConfigurationType === "global-title-builder") {
      setIsGlobalTitleBuilderOpen(true);
    } else {
      setIsBatchTitleBuilderOpen(true);
    }
  };

  // Handle saving batch title builder configuration
  const handleSaveBatchTitleBuilder = (configuration) => {
    setBatchTitleBuilderConfig(configuration);
    // Also update titleBuilder for backward compatibility (convert new format to old format)
    const converted = configuration
      .map((item) => {
        if (item.type === "field-name") {
          return { field: item.value, prefix: "", suffix: "" };
        } else if (item.type === "string") {
          // String items are handled separately, but we'll merge them with adjacent field-name items
          return null; // Will be handled in the conversion logic
        }
        return null;
      })
      .filter((item) => item !== null);

    // For now, just store the configuration and let buildFinalizePayload handle it
    setTitleBuilder(converted);
  };

  // Handle closing batch title builder modal
  const handleCloseBatchTitleBuilder = () => {
    setIsBatchTitleBuilderOpen(false);
  };

  // Handle closing global title builder modal
  const handleCloseGlobalTitleBuilder = () => {
    setIsGlobalTitleBuilderOpen(false);
    setGlobalTitleBuilderError("");
    setGlobalTitleBuilderSuccess("");
  };

  // Extract error message from a fetch Response
  const extractErrorMessage = async (response) => {
    try {
      const text = await response.text();
      if (!text) return `${response.status} ${response.statusText}`;
      try {
        const data = JSON.parse(text);
        return (
          data?.error ||
          data?.message ||
          data?.detail ||
          (typeof data === "string" ? data : JSON.stringify(data))
        );
      } catch (_e) {
        return text;
      }
    } catch (_err) {
      return "Request failed";
    }
  };

  // ---------------- Front-end validations ----------------
  const MAX_FILE_MB = 25;
  const validateFiles = (files) => {
    if (!files || files.length === 0) {
      return { ok: false, message: "Please select files to upload" };
    }

    // Only images
    const nonImages = files.filter(
      (f) => !(f?.type || "").startsWith("image/")
    );
    if (nonImages.length > 0) {
      return { ok: false, message: "Only image files are allowed" };
    }

    // Size limit per file
    const tooLarge = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooLarge) {
      return { ok: false, message: `Each file must be <= ${MAX_FILE_MB} MB` };
    }

    // Even number of files (front/back for each card)
    if (files.length % 2 !== 0) {
      return {
        ok: false,
        message: "Number of files must be even (front & back for each card).",
      };
    }

    return { ok: true };
  };

  const handleUpload = async () => {
    const LOG = "[CardUpload:Upload]";

    const validation = validateFiles(selectedFiles);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    const uploadId = crypto.randomUUID();
    const filesToUpload = [...selectedFiles];
    const batchLabel = batchName || "Default Batch";
    const totalChunksAll = filesToUpload.reduce(
      (sum, file) => sum + Math.ceil(file.size / CHUNK_SIZE),
      0
    );

    setIsUploading(true);
    setError("");
    setUploadStatus("");

    startChunkedUpload({
      uploadId,
      batchName: batchLabel,
      totalChunks: totalChunksAll,
      fileCount: filesToUpload.length,
    });

    try {
      const token = localStorage.getItem("access");
      if (!token) throw new Error("No access token found. Please login again.");

      const { batchId } = await uploadFilesInChunks({
        files: filesToUpload,
        batchName: batchLabel,
        token,
        apiBaseUrl: API_BASE_URL,
        onChunkProgress: ({
          chunksUploaded,
          totalChunks,
          fileName,
          fileChunksUploaded,
          fileChunksTotal,
          serverProgress,
        }) => {
          updateChunkProgress(
            uploadId,
            chunksUploaded,
            totalChunks,
            serverProgress
          );
          setUploadProgress((prev) => ({
            ...prev,
            [fileName]: {
              uploaded: fileChunksUploaded,
              total: fileChunksTotal,
            },
          }));
        },
      });

      const finalizePayload = buildFinalizePayload();
      const finalizeData = await finalizeCardUpload({
        batchName: finalizePayload.batch_name,
        defaultMetadata: finalizePayload.default_metadata,
        titleBuilderData: finalizePayload.titleBuilderData,
        token,
        apiBaseUrl: API_BASE_URL,
      });

      const resolvedBatchId = finalizeData?.batch_id ?? batchId;
      if (resolvedBatchId == null) {
        throw new Error("Server did not return a batch_id for this upload.");
      }

      startBatchProcessing({
        uploadId,
        batchId: resolvedBatchId,
        taskId: finalizeData?.task_id,
        batchName: batchLabel,
        status: finalizeData?.status || "processing",
        progress: finalizeData?.progress ?? 50,
      });

      setUploadStatus(
        `${filesToUpload.length} file(s) sent — processing continues in the background.`
      );
      setSelectedFiles([]);
      setUploadProgress({});
      document.getElementById("file-input").value = "";
    } catch (err) {
      console.error(LOG, "Upload failed", err);
      setError(err.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setError("");
    setUploadStatus("");
    document.getElementById("file-input").value = "";
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(
      selectedFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleViewImage = (file) => {
    setMagnifiedImage(URL.createObjectURL(file));
  };

  const handleDeleteImage = (indexToRemove) => {
    removeFile(indexToRemove);
  };

  const closeMagnifiedView = () => {
    if (magnifiedImage) {
      URL.revokeObjectURL(magnifiedImage);
    }
    setMagnifiedImage(null);
  };

  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file && typeof file === "object" && file.name) {
          URL.revokeObjectURL(URL.createObjectURL(file));
        }
      });
    };
  }, [selectedFiles]);

  // Auto-scroll to upload button when files are added
  useEffect(() => {
    if (
      selectedFiles.length > prevFilesCountRef.current &&
      selectedFiles.length > 0
    ) {
      requestAnimationFrame(() => {
        uploadActionsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });
    }
    prevFilesCountRef.current = selectedFiles.length;
  }, [selectedFiles]);

  // Auto-scroll to error message when present
  useEffect(() => {
    if (error && errorRef.current) {
      try {
        errorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } catch (_) {}
    }
  }, [error]);

  // Check if all required fields are filled
  const areAllFieldsFilled = () => {
    // Check batch name
    if (!batchName || batchName.trim() === "") {
      return false;
    }

    // Check title builder configuration based on type
    if (titleConfigurationType === "global-title-builder") {
      if (!globalTitleBuilderConfig || globalTitleBuilderConfig.length === 0) {
        return false;
      }
    } else if (titleConfigurationType === "batch-specific-title-builder") {
      if (!batchTitleBuilderConfig || batchTitleBuilderConfig.length === 0) {
        return false;
      }
    }

    // Check files
    if (selectedFiles.length === 0) {
      return false;
    }

    return true;
  };

  // Get missing fields message
  const getMissingFieldsMessage = () => {
    const missing = [];

    if (!batchName || batchName.trim() === "") {
      missing.push("Batch Name");
    }

    if (titleConfigurationType === "global-title-builder") {
      if (!globalTitleBuilderConfig || globalTitleBuilderConfig.length === 0) {
        missing.push("Global Title Builder Configuration");
      }
    } else if (titleConfigurationType === "batch-specific-title-builder") {
      if (!batchTitleBuilderConfig || batchTitleBuilderConfig.length === 0) {
        missing.push("Batch Title Builder Configuration");
      }
    }

    if (selectedFiles.length === 0) {
      missing.push("Files");
    }

    if (missing.length === 0) return null;

    return `Please fill in the following required fields: ${missing.join(
      ", "
    )}`;
  };

  return (
    <UploadSection>
      <UploadContainer>
        <InputGroup>
          <Label htmlFor="batch-name">
            Batch Name <span style={{ color: "#ef4444" }}>*</span>
          </Label>
          <Input
            id="batch-name"
            type="text"
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            placeholder="e.g. September Batch"
            required
          />
        </InputGroup>

        <InputGroup>
          <Label style={{ marginBottom: "0.75rem", display: "block" }}>
            Presets <span style={{ color: "#ef4444" }}>*</span>
          </Label>
          <PresetsWrapper>
            <PresetsOptions>
              <PresetSelectButton
                onClick={handleSetPresets}
                disabled={isUploading}
                type="button"
              >
                <PresetSelectText>
                  {selectedPreset
                    ? selectedPreset.name
                    : "Select from the list"}
                </PresetSelectText>
                <ChevronDown size={18} />
              </PresetSelectButton>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0 0.5rem",
                  color: "#6b7280",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                or
              </div>
              <BatchSpecificButton
                onClick={handleOpenBatchSpecificPresetModal}
                disabled={isUploading}
                type="button"
              >
                Batch specific presets
              </BatchSpecificButton>
            </PresetsOptions>
            {defaultMetadata && (
              <>
                <SelectedPresetBadge>
                  <span
                    style={{
                      fontWeight: 500,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedPreset
                      ? selectedPreset.name
                      : batchSpecificPresetName || "Batch Specific Preset"}
                  </span>
                  <BadgeRemoveButton
                    onClick={handleClearPresets}
                    disabled={isUploading}
                    title="Clear preset"
                  >
                    <X size={16} />
                  </BadgeRemoveButton>
                </SelectedPresetBadge>
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                >
                  <Label
                    style={{
                      marginBottom: "0.75rem",
                      display: "block",
                      fontWeight: 600,
                    }}
                  >
                    Preset Preview:
                  </Label>
                  <PreviewBox
                    dangerouslySetInnerHTML={{
                      __html: generatePresetPreview(defaultMetadata),
                    }}
                  />
                </div>
              </>
            )}
          </PresetsWrapper>
        </InputGroup>

        <InputGroup>
          <Label style={{ marginBottom: "0.75rem", display: "block" }}>
            Title Builder Configuration Type:{" "}
            <span style={{ color: "#ef4444" }}>*</span>
          </Label>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1.5rem",
              marginBottom: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="titleConfigurationType"
                  value="global-title-builder"
                  checked={titleConfigurationType === "global-title-builder"}
                  onChange={(e) => setTitleConfigurationType(e.target.value)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                  Global Title Builder
                </span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="titleConfigurationType"
                  value="batch-specific-title-builder"
                  checked={
                    titleConfigurationType === "batch-specific-title-builder"
                  }
                  onChange={(e) => setTitleConfigurationType(e.target.value)}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                  Batch Specific Title Builder
                </span>
              </label>
            </div>
            <Button
              onClick={handleOpenTitleBuilder}
              className="secondary"
              type="button"
              style={{ padding: "0.625rem 1.25rem", fontSize: "0.875rem" }}
            >
              {titleConfigurationType === "global-title-builder"
                ? "Configure Global Title Builder"
                : "Configure Batch Title Builder"}
            </Button>
          </div>
          {titleConfigurationType === "global-title-builder" &&
            globalTitleBuilderConfig.length > 0 && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  background: "transparent",
                  border: "1px solid rgba(17, 24, 39, 0.08)",
                  borderRadius: "12px",
                  boxShadow:
                    "0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                }}
              >
                <Label
                  style={{
                    marginBottom: "0.75rem",
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  Global Title Builder Preview:
                </Label>
                <PreviewBox
                  dangerouslySetInnerHTML={{
                    __html: generatePreviewFromConfig(globalTitleBuilderConfig),
                  }}
                />
              </div>
            )}
          {titleConfigurationType === "batch-specific-title-builder" &&
            batchTitleBuilderConfig.length > 0 && (
              <div
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  background: "transparent",
                  border: "1px solid rgba(17, 24, 39, 0.08)",
                  borderRadius: "12px",
                  boxShadow:
                    "0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                }}
              >
                <Label
                  style={{
                    marginBottom: "0.75rem",
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  Batch Title Builder Preview:
                </Label>
                <PreviewBox
                  dangerouslySetInnerHTML={{
                    __html: generatePreviewFromConfig(batchTitleBuilderConfig),
                  }}
                />
              </div>
            )}
        </InputGroup>

        <InputGroup>
          <Label htmlFor="file-input">
            Choose Files (Multiple) <span style={{ color: "#ef4444" }}>*</span>
          </Label>
          <Dropzone
            $isDragging={isDraggingFiles}
            onClick={handleDropzoneClick}
            onDragOver={handleDropzoneDragOver}
            onDragLeave={handleDropzoneDragLeave}
            onDrop={handleDropzoneDrop}
          >
            <DropzoneText>
              Click or drag here to upload
              <DropzoneSubtext>
                {selectedFiles.length > 0
                  ? `${selectedFiles.length} file(s) selected`
                  : "You can select multiple images at once"}
              </DropzoneSubtext>
            </DropzoneText>
            <HiddenFileInput
              id="file-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              required
            />
          </Dropzone>
        </InputGroup>

        {selectedFiles.length > 0 && (
          <ImagePreviewSection>
            <ImagePreviewTitle>
              Selected Images ({selectedFiles.length})
            </ImagePreviewTitle>
            <ImagePreviewGrid>
              {Array.from(
                { length: Math.ceil(selectedFiles.length / 2) },
                (_, pairIndex) => {
                  const startIndex = pairIndex * 2;
                  const pairFiles = selectedFiles.slice(
                    startIndex,
                    startIndex + 2
                  );

                  return (
                    <CardPairContainer key={pairIndex}>
                      {pairFiles.map((file, fileIndex) => {
                        const index = startIndex + fileIndex;
                        return (
                          <ImagePreviewItem key={index}>
                            <ImagePreview
                              src={URL.createObjectURL(file)}
                              alt={`Preview ${index + 1}`}
                            />
                            <HoverOverlay className="hover-overlay">
                              <ActionButton
                                className="view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewImage(file);
                                }}
                                disabled={isUploading}
                                title="View full size"
                              >
                                <HiOutlineViewfinderCircle />
                              </ActionButton>
                              <ActionButton
                                className="delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage(index);
                                }}
                                disabled={isUploading}
                                title="Delete image"
                              >
                                <FiTrash />
                              </ActionButton>
                            </HoverOverlay>
                            <ImageDetails>
                              <ImageName title={file.name}>
                                {file.name}
                              </ImageName>
                              <ImageSize>
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </ImageSize>
                              {uploadProgress[file.name] && (
                                <ProgressContainer>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#6b7280",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {Math.round(
                                      (uploadProgress[file.name].uploaded /
                                        uploadProgress[file.name].total) *
                                        100
                                    )}
                                    %
                                  </div>
                                  <ProgressBarBackground>
                                    <ProgressBarFill
                                      width={
                                        (uploadProgress[file.name].uploaded /
                                          uploadProgress[file.name].total) *
                                        100
                                      }
                                    />
                                  </ProgressBarBackground>
                                </ProgressContainer>
                              )}
                            </ImageDetails>
                          </ImagePreviewItem>
                        );
                      })}
                    </CardPairContainer>
                  );
                }
              )}
            </ImagePreviewGrid>
          </ImagePreviewSection>
        )}

        <Actions ref={uploadActionsRef}>
          {!areAllFieldsFilled() && !isUploading && (
            <div
              style={{
                fontSize: "0.875rem",
                color: "#6b7280",
                fontStyle: "italic",
                marginRight: "auto",
              }}
            >
              {getMissingFieldsMessage()}
            </div>
          )}
          <Button
            onClick={handleUpload}
            disabled={!areAllFieldsFilled() || isUploading}
            className="primary"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>

          <Button
            onClick={handleClear}
            disabled={selectedFiles.length === 0 || isUploading}
            className="secondary"
          >
            Clear All
          </Button>
        </Actions>

        {error && <ErrorMessage ref={errorRef}>{error}</ErrorMessage>}
        {uploadStatus && <SuccessMessage>{uploadStatus}</SuccessMessage>}
      </UploadContainer>

      {/* Batch Title Builder Modal */}
      <BatchTitleBuilder
        isOpen={isBatchTitleBuilderOpen}
        onClose={handleCloseBatchTitleBuilder}
        availableFields={availableFields}
        configuration={batchTitleBuilderConfig}
        onSave={handleSaveBatchTitleBuilder}
      />

      {/* Preset Selection Modal */}
      {isPresetModalOpen && (
        <ModalOverlay onClick={handleClosePresetModal}>
          <PresetModalContainer onClick={(e) => e.stopPropagation()}>
            <PresetModalHeader>
              <ModalTitle style={{ margin: 0, fontSize: "1.125rem" }}>
                Select Preset
              </ModalTitle>
              <PresetModalCloseButton
                onClick={handleClosePresetModal}
                title="Close"
              >
                <X size={18} />
              </PresetModalCloseButton>
            </PresetModalHeader>
            <PresetModalSearchSection>
              <PresetModalSearch>
                <SearchIconWrapper>
                  <Search size={16} />
                </SearchIconWrapper>
                <PresetModalSearchInput
                  type="text"
                  placeholder="Search presets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                {searchQuery && (
                  <PresetCount>
                    {filteredPresets.length}{" "}
                    {filteredPresets.length === 1 ? "result" : "results"}
                  </PresetCount>
                )}
              </PresetModalSearch>
              <CreatePresetLink
                onClick={handleOpenCreatePresetModal}
                type="button"
              >
                Create New Preset
              </CreatePresetLink>
            </PresetModalSearchSection>
            <PresetModalContent>
              <PresetModalList>
                {filteredPresets.length === 0 ? (
                  <PresetModalEmptyState>
                    {searchQuery
                      ? "No presets found matching your search"
                      : "No presets available"}
                  </PresetModalEmptyState>
                ) : (
                  filteredPresets.map((preset) => (
                    <PresetModalItem
                      key={preset.id}
                      onClick={() => handleSelectPreset(preset)}
                      className={
                        selectedPreset?.id === preset.id ? "selected" : ""
                      }
                    >
                      {preset.name || "Unnamed Preset"}
                    </PresetModalItem>
                  ))
                )}
              </PresetModalList>
            </PresetModalContent>
            <PresetModalFooter>
              <Button
                onClick={handleClosePresetModal}
                className="secondary small"
              >
                Cancel
              </Button>
            </PresetModalFooter>
          </PresetModalContainer>
        </ModalOverlay>
      )}

      {/* Batch Specific Preset Modal */}
      {isBatchSpecificPresetModalOpen && (
        <ModalOverlay onClick={handleCloseBatchSpecificPresetModal}>
          <BatchSpecificPresetModalContainer
            onClick={(e) => e.stopPropagation()}
          >
            <BatchSpecificPresetModalHeader>
              <ModalTitle>Preset Details</ModalTitle>
            </BatchSpecificPresetModalHeader>
            <BatchSpecificPresetModalContent>
              <div>
                <PresetsSubtitle>eBay Item Specifics</PresetsSubtitle>
                <Grid2>
                  <InputGroup>
                    <Label>Category</Label>
                    <Select
                      value={batchSpecificPresetFormData.category}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "category",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Sport Trading Card">
                        Sport Trading Card
                      </option>
                      <option value="Non-Sport Trading Card">
                        Non-Sport Trading Card
                      </option>
                      <option value="CCG Individual Card">
                        CCG Individual Card
                      </option>
                      <option value="Collectible Sports Sticker Singles">
                        Collectible Sports Sticker Singles
                      </option>
                      <option value="Collectible Non-Sport Sticker Singles">
                        Collectible Non-Sport Sticker Singles
                      </option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Manufacturer</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.manufacturer}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "manufacturer",
                          e.target.value
                        )
                      }
                      placeholder="Enter manufacturer"
                    />
                  </InputGroup>
                  {CATEGORY_SPECIFIC_FIELD_LABEL[batchSpecificPresetFormData.category] && (
                    <InputGroup>
                      <Label>
                        {CATEGORY_SPECIFIC_FIELD_LABEL[batchSpecificPresetFormData.category]}
                      </Label>
                      <Input
                        type="text"
                        value={batchSpecificPresetFormData.sportGameFranchise}
                        onChange={(e) =>
                          handleBatchSpecificPresetInputChange(
                            "sportGameFranchise",
                            e.target.value
                          )
                        }
                        placeholder={`Enter ${CATEGORY_SPECIFIC_FIELD_LABEL[
                          batchSpecificPresetFormData.category
                        ].toLowerCase()}`}
                      />
                    </InputGroup>
                  )}
                  <InputGroup>
                    <Label>Autographed</Label>
                    <Select
                      value={batchSpecificPresetFormData.autographed}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "autographed",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Graded</Label>
                    <Select
                      value={batchSpecificPresetFormData.graded}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "graded",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Condition</Label>
                    <Select
                      value={batchSpecificPresetFormData.condition}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "condition",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Condition</option>
                      {getConditionOptions(
                        batchSpecificPresetFormData.category
                      ).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Language</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.language}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "language",
                          e.target.value
                        )
                      }
                      placeholder="Enter language"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Set</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.set}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "set",
                          e.target.value
                        )
                      }
                      placeholder="Enter set"
                    />
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Year Manufactured</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.yearManufactured}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "yearManufactured",
                          e.target.value
                        )
                      }
                      placeholder="Enter year manufactured"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Vintage</Label>
                    <Select
                      value={batchSpecificPresetFormData.vintage}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "vintage",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Condition Description</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.conditionDescription}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "conditionDescription",
                          e.target.value
                        )
                      }
                      placeholder="Enter condition description"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Country of Origin</Label>
                    <Input
                      type="text"
                      value={batchSpecificPresetFormData.country}
                      onChange={(e) =>
                        handleBatchSpecificPresetInputChange(
                          "country",
                          e.target.value
                        )
                      }
                      placeholder="Enter country"
                    />
                  </InputGroup>
                </Grid2>
              </div>
            </BatchSpecificPresetModalContent>
            <BatchSpecificPresetModalFooter>
              <Button
                onClick={handleCloseBatchSpecificPresetModal}
                className="secondary small"
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBatchSpecificPreset}
                className="primary small"
                disabled={isUploading}
              >
                Update Preset
              </Button>
            </BatchSpecificPresetModalFooter>
          </BatchSpecificPresetModalContainer>
        </ModalOverlay>
      )}

      {/* Create Preset Modal */}
      {isCreatePresetModalOpen && (
        <ModalOverlay onClick={handleCloseCreatePresetModal}>
          <BatchSpecificPresetModalContainer
            onClick={(e) => e.stopPropagation()}
          >
            <BatchSpecificPresetModalHeader>
              <ModalTitle>Create New Preset</ModalTitle>
            </BatchSpecificPresetModalHeader>
            <BatchSpecificPresetModalContent>
              <div>
                <InputGroup>
                  <Label>
                    Preset Name <span style={{ color: "#ef4444" }}>*</span>
                  </Label>
                  <Input
                    type="text"
                    value={createPresetFormData.presetName}
                    onChange={(e) =>
                      handleCreatePresetInputChange(
                        "presetName",
                        e.target.value
                      )
                    }
                    placeholder="Enter preset name"
                  />
                </InputGroup>
              </div>

              <div>
                <PresetsSubtitle>eBay Item Specifics</PresetsSubtitle>
                <Grid2>
                  <InputGroup>
                    <Label>Category</Label>
                    <Select
                      value={createPresetFormData.category}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "category",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Sport Trading Card">
                        Sport Trading Card
                      </option>
                      <option value="Non-Sport Trading Card">
                        Non-Sport Trading Card
                      </option>
                      <option value="CCG Individual Card">
                        CCG Individual Card
                      </option>
                      <option value="Collectible Sports Sticker Singles">
                        Collectible Sports Sticker Singles
                      </option>
                      <option value="Collectible Non-Sport Sticker Singles">
                        Collectible Non-Sport Sticker Singles
                      </option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Manufacturer</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.manufacturer}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "manufacturer",
                          e.target.value
                        )
                      }
                      placeholder="Enter manufacturer"
                    />
                  </InputGroup>
                  {CATEGORY_SPECIFIC_FIELD_LABEL[createPresetFormData.category] && (
                    <InputGroup>
                      <Label>
                        {CATEGORY_SPECIFIC_FIELD_LABEL[createPresetFormData.category]}
                      </Label>
                      <Input
                        type="text"
                        value={createPresetFormData.sportGameFranchise}
                        onChange={(e) =>
                          handleCreatePresetInputChange(
                            "sportGameFranchise",
                            e.target.value
                          )
                        }
                        placeholder={`Enter ${CATEGORY_SPECIFIC_FIELD_LABEL[
                          createPresetFormData.category
                        ].toLowerCase()}`}
                      />
                    </InputGroup>
                  )}
                  <InputGroup>
                    <Label>Autographed</Label>
                    <Select
                      value={createPresetFormData.autographed}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "autographed",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Graded</Label>
                    <Select
                      value={createPresetFormData.graded}
                      onChange={(e) =>
                        handleCreatePresetInputChange("graded", e.target.value)
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                  <InputGroup>
                    <Label>Condition</Label>
                    <Select
                      value={createPresetFormData.condition}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "condition",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Condition</option>
                      {getConditionOptions(createPresetFormData.category).map(
                        (option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        )
                      )}
                    </Select>
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Language</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.language}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "language",
                          e.target.value
                        )
                      }
                      placeholder="Enter language"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Set</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.set}
                      onChange={(e) =>
                        handleCreatePresetInputChange("set", e.target.value)
                      }
                      placeholder="Enter set"
                    />
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Year Manufactured</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.yearManufactured}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "yearManufactured",
                          e.target.value
                        )
                      }
                      placeholder="Enter year manufactured"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Vintage</Label>
                    <Select
                      value={createPresetFormData.vintage}
                      onChange={(e) =>
                        handleCreatePresetInputChange("vintage", e.target.value)
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  </InputGroup>
                </Grid2>
                <Grid2>
                  <InputGroup>
                    <Label>Condition Description</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.conditionDescription}
                      onChange={(e) =>
                        handleCreatePresetInputChange(
                          "conditionDescription",
                          e.target.value
                        )
                      }
                      placeholder="Enter condition description"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Country of Origin</Label>
                    <Input
                      type="text"
                      value={createPresetFormData.country}
                      onChange={(e) =>
                        handleCreatePresetInputChange("country", e.target.value)
                      }
                      placeholder="Enter country"
                    />
                  </InputGroup>
                </Grid2>
              </div>
            </BatchSpecificPresetModalContent>
            <BatchSpecificPresetModalFooter>
              <Button
                onClick={handleCloseCreatePresetModal}
                className="secondary small"
                disabled={isSavingPreset}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCreatePreset}
                className="primary small"
                disabled={
                  isSavingPreset || !createPresetFormData.presetName.trim()
                }
              >
                {isSavingPreset ? "Creating..." : "Create Preset"}
              </Button>
            </BatchSpecificPresetModalFooter>
          </BatchSpecificPresetModalContainer>
        </ModalOverlay>
      )}

      {/* Global Title Builder Modal */}
      {isGlobalTitleBuilderOpen && (
        <ModalOverlay onClick={handleCloseGlobalTitleBuilder}>
          <GlobalTitleBuilderModalContainer
            onClick={(e) => e.stopPropagation()}
          >
            <GlobalTitleBuilderModalHeader>
              <ModalTitle>Global Title Builder</ModalTitle>
            </GlobalTitleBuilderModalHeader>
            <GlobalTitleBuilderModalContent>
              <div>
                <Label
                  style={{
                    marginBottom: "0.75rem",
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  Title Input
                </Label>
                <GlobalTitleInputContainer
                  isDragging={
                    isDraggingOverGlobalInput && isGlobalTitleBuilderReady
                  }
                  onDragOver={handleGlobalContainerDragOver}
                  onDrop={handleGlobalContainerDrop}
                  style={{
                    opacity: isGlobalTitleBuilderReady ? 1 : 0.7,
                    pointerEvents: isGlobalTitleBuilderReady ? "auto" : "none",
                  }}
                >
                  <GlobalTitleInput
                    ref={globalTitleInputRef}
                    contentEditable={isGlobalTitleBuilderReady}
                    onInput={handleGlobalTitleInput}
                    onPaste={handleGlobalPaste}
                    onDragOver={handleGlobalDragOver}
                    onDragLeave={handleGlobalDragLeave}
                    onDrop={handleGlobalDrop}
                    data-placeholder="Type your title here or drag fields from below..."
                    suppressContentEditableWarning
                    style={{
                      pointerEvents: isGlobalTitleBuilderReady
                        ? "auto"
                        : "none",
                    }}
                  />
                </GlobalTitleInputContainer>
              </div>

              <div>
                <ColumnTitle>Available Fields</ColumnTitle>
                <GlobalAvailableFieldsList>
                  {availableFields.map((field) => (
                    <GlobalFieldItem
                      key={field}
                      draggable={isGlobalTitleBuilderReady}
                      onDragStart={(e) => {
                        if (!isGlobalTitleBuilderReady) {
                          e.preventDefault();
                          return;
                        }
                        setDraggedField(field);
                        e.dataTransfer.setData("text/plain", field);
                        e.dataTransfer.effectAllowed = "copy";
                        if (e.target) {
                          e.target.style.opacity = "0.5";
                        }
                      }}
                      onDragEnd={(e) => {
                        setDraggedField(null);
                        if (e.target) {
                          e.target.style.opacity = "1";
                        }
                      }}
                      className={draggedField === field ? "dragging" : ""}
                      style={{
                        opacity: isGlobalTitleBuilderReady ? 1 : 0.5,
                        cursor: isGlobalTitleBuilderReady
                          ? "move"
                          : "not-allowed",
                      }}
                    >
                      {field}
                    </GlobalFieldItem>
                  ))}
                </GlobalAvailableFieldsList>
              </div>

              <div>
                <ColumnTitle>Preview</ColumnTitle>
                <GlobalPreviewBox
                  dangerouslySetInnerHTML={{
                    __html:
                      globalTitleBuilderPreview ||
                      "Your title preview will appear here...",
                  }}
                />
              </div>

              {globalTitleBuilderError && (
                <ErrorMessage>{globalTitleBuilderError}</ErrorMessage>
              )}
              {globalTitleBuilderSuccess && (
                <SuccessMessage>{globalTitleBuilderSuccess}</SuccessMessage>
              )}
            </GlobalTitleBuilderModalContent>
            <GlobalTitleBuilderModalFooter>
              <Button
                onClick={handleClearGlobalTitleBuilder}
                className="secondary small"
                disabled={isSavingGlobalTitleBuilder}
              >
                Clear All
              </Button>
              <Button
                onClick={handleSaveGlobalTitleBuilder}
                className="primary small"
                disabled={
                  isSavingGlobalTitleBuilder ||
                  formatGlobalConfiguration().length === 0
                }
              >
                {isSavingGlobalTitleBuilder ? "Saving..." : "Save Settings"}
              </Button>
              <Button
                onClick={handleCloseGlobalTitleBuilder}
                className="secondary small"
                disabled={isSavingGlobalTitleBuilder}
              >
                Close
              </Button>
            </GlobalTitleBuilderModalFooter>
          </GlobalTitleBuilderModalContainer>
        </ModalOverlay>
      )}

      {/* Magnified View Modal */}
      {magnifiedImage && (
        <MagnifiedViewOverlay onClick={closeMagnifiedView}>
          <MagnifiedViewContainer onClick={(e) => e.stopPropagation()}>
            <MagnifiedImage src={magnifiedImage} alt="Magnified view" />
            <CloseButton onClick={closeMagnifiedView}>×</CloseButton>
          </MagnifiedViewContainer>
        </MagnifiedViewOverlay>
      )}
    </UploadSection>
  );
}

export default Upload;
